import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CornerDownLeft, FileText, Fish, Package, Search, X } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import { searchSite, type SearchCategory } from "../data/searchIndex";
import { trackEvent } from "../utils/analytics";

const CATEGORY_ICON: Partial<Record<SearchCategory, typeof FileText>> = {
  Menu: Fish,
  Product: Package,
  Page: FileText,
};

/** Shown before anything is typed: real things to search for. */
const TRY_SEARCHING = ["Pomfret", "Prawns", "Crab", "Products", "Contact"];

const NO_RESULTS_SUGGESTIONS = [
  { label: "The Catch (menu)", path: "/menu" },
  { label: "Products & Services", path: "/products" },
  { label: "Contact", path: "/contact" },
];

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchModal({ open, onClose }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();

  const results = useMemo(() => searchSite(query), [query]);
  // Clamped so a query that shrinks the result count can never leave the
  // highlight pointing past the end for even one render.
  const safeActiveIndex = Math.min(activeIndex, Math.max(0, results.length - 1));

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Focus after the entrance transition starts so mobile keyboards don't
      // fight the modal's own mount animation.
      const id = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  const select = (path: string) => {
    trackEvent("search_result_select", { path, query });
    navigate(path);
    onClose();
  };

  // Window-level (not just the input's onKeyDown) so Escape/arrows/Enter
  // keep working once Tab has moved focus to a result row or the close
  // button — previously they silently stopped doing anything the moment
  // focus left the text field. Also implements a basic Tab focus trap so
  // keyboard users can't tab out into the dimmed page behind the overlay.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const target = results[safeActiveIndex];
        if (target) select(target.path);
      } else if (e.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, safeActiveIndex]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.15 }}
          className="fixed inset-0 z-[60] flex items-start justify-center bg-ocean-950/70 px-4 pt-24 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site search"
            initial={reducedMotion ? false : { opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-ocean-900"
          >
            <div className="flex items-center gap-3 border-b border-ocean-900/10 px-4 py-3 dark:border-white/10">
              <Search className="h-4 w-4 shrink-0 text-ocean-800/50 dark:text-foam-100/50" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the menu, products or pages…"
                aria-label="Search ZenFresh"
                className="w-full bg-transparent text-sm text-ocean-950 placeholder:text-ocean-800/40 focus:outline-none dark:text-foam-50 dark:placeholder:text-foam-100/40"
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close search"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-ocean-800/50 hover:bg-ocean-900/5 dark:text-foam-100/50 dark:hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto py-2">
              {!query && (
                <div className="px-4 py-4">
                  <p className="text-xs text-ocean-800/60 dark:text-foam-100/60">Search the menu, our products and services, or any page. Try:</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {TRY_SEARCHING.map((term) => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => setQuery(term)}
                        className="rounded-full bg-teal-400/15 px-3 py-1 text-xs font-medium text-teal-600 hover:bg-teal-400/25"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {query && results.length === 0 && (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm text-ocean-800/60 dark:text-foam-100/60">No results for &ldquo;{query}&rdquo;.</p>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {NO_RESULTS_SUGGESTIONS.map((s) => (
                      <button
                        key={s.path}
                        onClick={() => select(s.path)}
                        className="rounded-full bg-teal-400/15 px-3 py-1 text-xs font-medium text-teal-600 hover:bg-teal-400/25"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {results.map((r, i) => {
                const Icon = CATEGORY_ICON[r.category] ?? FileText;
                return (
                  <button
                    key={r.id}
                    onClick={() => select(r.path)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                      i === safeActiveIndex ? "bg-teal-400/10" : ""
                    }`}
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" strokeWidth={1.5} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ocean-950 dark:text-foam-50">{r.title}</span>
                      {r.subtitle && (
                        <span className="block truncate text-xs text-ocean-800/50 dark:text-foam-100/50">{r.subtitle}</span>
                      )}
                    </span>
                    <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-ocean-800/30 dark:text-foam-100/30">
                      {r.category}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between border-t border-ocean-900/10 px-4 py-2 text-[11px] text-ocean-800/40 dark:border-white/10 dark:text-foam-100/40">
              <span className="flex items-center gap-1">
                <CornerDownLeft className="h-3 w-3" /> to select
              </span>
              <span>Esc to close</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
